<template>
  <div class="card accordion-multi" data-panel-group :data-project-id="data.id">
    <div class="card-header" :id="headingId">
      <button
        class="btn-accordion"
        :class="[statusKeyComputed, 'collapsed']"
        type="button"
        v-b-toggle="collapseId"
      >
        <div class="market-project__content">
          <h3 class="market-project__title">
            <a href="#" @click.stop.prevent="goToDetails">
              {{ data.name }}
            </a>
          </h3>

          <div v-if="marketplaceName">
            Marketplace: <strong>{{ marketplaceName }}</strong>
          </div>

          <ul class="specs-grid">
            <li>
              Country:
              <strong>{{ countryName }}</strong>
            </li>

            <li v-for="spec in dynamicSpecs" :key="spec.key">
              {{ spec.label }}:
              <strong>{{ spec.value }}</strong>
            </li>
          </ul>
        </div>

        <div class="market-project__actions" data-panel-group>
          <div class="market-project__info offer">
            <span class="deadline-info">
              <i class="icon-calendar"></i>
              {{ itemType === 'product' ? 'Delivery date:' : 'Offer deadline:' }}
              <strong>{{ deadlineText }}</strong>
            </span>
          </div>

          <div class="status-multi">
            <h6>Status:</h6>
            <span
              v-for="status in statusBadges"
              :key="status.key"
              class="status"
              :class="status.key"
            >
              {{ status.label }}
            </span>
          </div>

          <div class="market-project__buttons buttons-group">
            <a class="btn-action btn-reject" href="#" @click.stop.prevent="unsaveProject">
              <i class="icon-unsave"></i>
              Unsave
            </a>

            <a class="btn-action" href="#" @click.stop.prevent="goToDetails">
              <i class="icon-info"></i>
              See Details
            </a>

            <div
              v-if="canShowSendMessage"
              class="btn-action"
              role="button"
              tabindex="0"
              @click.stop.prevent="openMessages($event)"
            >
              <i class="icon-chat"></i>
              Send message
            </div>

            <a class="btn-action" href="#" @click.stop.prevent="makeOffer">
              <i class="icon-coffer"></i>
              Make an Offer
            </a>
          </div>
        </div>
      </button>
    </div>

    <b-collapse
      :id="collapseId"
      accordion="accordionSavedProjects"
      class="accordion-sublevel"
    >
      <div class="card-body">
        <div
          v-if="itemType === 'project'"
          class="project-messages-section"
          :data-project-id="data.id"
        >
          <h5 class="mb-2">Project messages</h5>
          <ProjectMessagesPanel
            :projectId="data.id"
            :messages="projectMessages"
            :readOnly="!canSellerMessageProject"
            @sent="onMessageSent"
          />
        </div>
        <div v-else class="text-center p-2">
          <small>No additional content.</small>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { BCollapse } from 'bootstrap-vue';
import MatchmakingService from '@/services/MatchmakingService';
import exception from '@/mixins/exception';
import ProjectMessagesPanel from '@/components/matchmaking/ProjectMessagesPanel.vue';
import {
  canSellerMessageProject,
  isSellerProjectMessage,
  projectMessagesCount,
} from '@/helpers/projectMessages';

export default {
  name: 'SavedProjectCard',
  components: { BCollapse, ProjectMessagesPanel },
  mixins: [exception],
  props: {
    data: {
      type: Object,
      required: true,
    },
    itemType: {
      type: String,
      default: 'project',
      validator: (v) => ['project', 'product'].includes(v),
    },
    statusKey: {
      type: String,
      default: 'saved',
    },
    unreadCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    collapseId() {
      return `collapse-saved-project-${this.data.id}`;
    },
    headingId() {
      return `heading-saved-project-${this.data.id}`;
    },

    statusKeyComputed() {
      return this.statusKey || 'saved';
    },
    offersCount() {
      return Number(this.data.offers_count || 0);
    },
    statusBadges() {
      const list = [];
      const status = Number(this.data.status);

      if (status === 2) list.push({ key: 'approved', label: 'Approved' });
      if (status === 4) list.push({ key: 'expired', label: 'Expired' });
      if (this.data.saved !== false) list.push({ key: 'saved', label: 'Saved' });
      if (this.offersCount > 0) list.push({ key: 'offered', label: 'Offered' });

      if (!list.length) {
        const labels = {
          saved: 'Saved',
          threads: 'Message threads',
          approved: 'Approved',
          counteroffered: 'Counter offered',
          all: 'Saved',
        };
        list.push({
          key: this.statusKeyComputed,
          label: labels[this.statusKeyComputed] || 'Saved',
        });
      }

      return list;
    },

    marketplaceName() {
      return (this.data.marketplace && this.data.marketplace.name)
        || this.data.marketplace_name
        || '';
    },

    countryName() {
      return (this.data.company_country && this.data.company_country.name)
        || this.data.company_country_name
        || (this.data.company_country && this.data.company_country.iso)
        || '';
    },

    deadlineText() {
      const d = this.data.deadline_at;
      if (!d || d === '∞') return 'not specified';
      return d;
    },

    projectMessages() {
      return Array.isArray(this.data.messages) ? this.data.messages : [];
    },

    canSellerMessageProject() {
      if (this.itemType !== 'project') return false;
      return canSellerMessageProject(this.data);
    },

    canShowSendMessage() {
      if (this.itemType !== 'project') return false;
      return this.canSellerMessageProject;
    },

    dynamicSpecs() {
      const out = [];
      const list = Array.isArray(this.data.data) ? this.data.data : [];

      list.forEach((field) => {
        if (!field || !field.name) return;

        const label = String(field.name).trim();
        if (!label) return;

        if (field.type === 'dropbox') {
          if (field.multiple) {
            const ids = Array.isArray(field.initValue) ? field.initValue : [];
            const values = (field.values && typeof field.values === 'object') ? field.values : {};

            const selected = ids
              .map((id) => values[id])
              .filter(Boolean);

            if (selected.length) {
              out.push({
                key: `${field.id || label}`,
                label,
                value: selected.join(', '),
              });
            }
            return;
          }

          if (field.initValue !== null && field.initValue !== undefined) {
            const values = (field.values && typeof field.values === 'object') ? field.values : {};
            const val = values[field.initValue];
            if (val) {
              out.push({
                key: `${field.id || label}`,
                label,
                value: val,
              });
            }
          }
          return;
        }

        if (field.initValue !== null
          && field.initValue !== undefined && String(field.initValue).trim()) {
          out.push({
            key: `${field.id || label}`,
            label,
            value: String(field.initValue).trim(),
          });
        }
      });

      return out;
    },
  },
  methods: {
    async unsaveProject() {
      try {
        if (this.itemType === 'product') {
          await MatchmakingService.unsaveProduct(this.data.id);
        } else {
          await MatchmakingService.sellerUnsaveProject({ project_id: this.data.id });
        }
        this.$emit('getProjects');
      } catch (error) {
        this.handleException(error);
      }
    },

    goToDetails() {
      if (this.itemType === 'product') {
        this.$router.push({
          name: 'profileMatchmakingSellerProductDetails',
          params: { id: this.data.id },
        }).catch(() => {});
        return;
      }
      const type = String(this.$route.params.type || 'seller');
      const iso = (this.data.company_country && this.data.company_country.iso)
        || this.data.company_country_iso
        || '';

      this.$router.push(`/profile/matchmaking/${type}/project/${iso}-${this.data.id}`);
    },

    openMessages(event) {
      const card = event && event.currentTarget
        ? event.currentTarget.closest('[data-project-id]')
        : null;
      const trigger = card
        ? card.querySelector(`[aria-controls="${this.collapseId}"]`)
        : null;
      if (trigger && trigger.getAttribute('aria-expanded') !== 'true') {
        trigger.click();
      } else {
        this.$root.$emit('bv::show::collapse', this.collapseId);
      }

      this.$nextTick(() => {
        setTimeout(() => {
          const section = this.$el
            && this.$el.querySelector(`.project-messages-section[data-project-id="${this.data.id}"]`);
          if (section && typeof section.scrollIntoView === 'function') {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 250);
      });
    },

    onMessageSent(message) {
      const list = Array.isArray(this.data.messages) ? this.data.messages : [];
      if (message && typeof message === 'object') {
        this.$set(this.data, 'messages', [...list, message]);
      }
      if (isSellerProjectMessage(message)) {
        const baseCount = Number(this.data.messages_count);
        const nextCount = Number.isFinite(baseCount) ? baseCount + 1 : projectMessagesCount(this.data);
        this.$set(this.data, 'messages_count', nextCount);
      }
    },

    makeOffer() {
      this.$emit('makeOffer', this.data);
    },
  },
};
</script>
